(ns ui.components.kanban-list.view
  "The column of a Kanban board, as in the UX mockup of the Dealflow pages. An open
   column is 300 px wide. It has a header with the title, the count in a secondary
   Badge, and the buttons that collapse the column and add an item. A collapsed
   column is a strip of 48 px with the button that expands it, the count and the
   title written upward. The width changes in 300 ms.

   The classes come from the generated `ui.components.kanban-list.zapkit-styles`,
   the Badge and the IconButton. `dom-overrides` has the classes of the mockup that
   ZapKit does not have. The mockup differs from the ZapKit `KanbanList` in the
   background, the gap between the title and the badge, and the gap between the
   buttons."
  (:require
   [ui.components.badge.view :as badge]
   [ui.components.icon-button.view :as icon-button]
   [ui.components.kanban-list.zapkit-styles :as styles]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.components.zapkit-variants :as variants]
   [ui.icons :as icons]
   [ui.util :as uu]))

(def dom-overrides
  "Classes of the mockup that the generated styles do not have. Each class string is
   copied from the DOM of the mockup. Each entry names the upstream key that it
   replaces or extends. When ZapKit has the class, use it and delete the entry here.

   `:section`, `:section-open` and `:section-collapsed` extend `[:root :base]`. The
   mockup background is `bg-primary`, and ZapKit has `bg-card-kanban-list`. The
   width changes over 300 ms and not 200 ms. The widths are the tokens of ZapKit 2.0.0,
   `kanban-list-width` (300 px) and `kanban-list-collapsed-width` (48 px). `[:root :base]`
   has `shrink-0` and `min-h-0` now, so the section does not add them.
   `:panel`, `:panel-visible` and `:panel-hidden` replace `[:panel ...]`. The panel
   of ZapKit has the width of its content and a gap. The panel of the mockup has
   the width of a column and fills its height.
   `:header` extends `[:header :base]` with the space under the header.
   `:title-group` replaces `[:title-group :base]`: the gap is 4 px and not 8 px.
   `:actions` replaces `[:actions :base]`: the buttons have no gap.
   `[:body :base]` fills the rest of the panel and scrolls, so the body has no override.
   `:strip`, `:strip-visible`, `:strip-hidden` and `:strip-button` replace the
   collapsed panel of ZapKit. The strip has 4 px of padding.
   `:strip-title` extends `[:title :collapsed :true]`: the title is written upward
   and the text turns, so that it reads from the bottom to the top."
  {:section           "tw2-h-full tw2-min-w-0 tw2-bg-bg-primary tw2-transition-[width] tw2-duration-300 tw2-ease-[cubic-bezier(0.22,1,0.36,1)]"
   :section-open      "tw2-w-kanban-list-width"
   :section-collapsed "tw2-w-kanban-list-collapsed-width"
   :panel             "tw2-absolute tw2-inset-y-0 tw2-left-0 tw2-flex tw2-h-full tw2-w-kanban-list-width tw2-flex-col tw2-p-kanban-padding tw2-transition-opacity tw2-duration-200 tw2-ease-out"
   :panel-visible     "tw2-opacity-100 tw2-delay-75"
   :panel-hidden      "tw2-pointer-events-none tw2-opacity-0 [&_*]:tw2-pointer-events-none"
   :header            "tw2-mb-2"
   :title-group       "tw2-flex tw2-min-w-0 tw2-items-center tw2-gap-1"
   :actions           "tw2-flex tw2-shrink-0 tw2-items-center"
   :strip             "tw2-absolute tw2-inset-y-0 tw2-left-0 tw2-z-10 tw2-flex tw2-w-kanban-list-collapsed-width tw2-flex-col tw2-items-center tw2-p-1 tw2-transition-opacity tw2-duration-200 tw2-ease-out"
   :strip-visible     "tw2-opacity-100 tw2-delay-150"
   :strip-hidden      "tw2-pointer-events-none tw2-opacity-0 [&_*]:tw2-pointer-events-none"
   :strip-button      "tw2-mt-1 tw2-flex tw2-min-h-0 tw2-w-full tw2-flex-1 tw2-cursor-pointer tw2-flex-col tw2-items-center"
   :strip-title       "tw2-mt-1 tw2-min-h-0 tw2-overflow-hidden tw2-rotate-180"})

(defn section-class
  "Return the classes of the column. A collapsed column is narrow. A column that a
   card is dragged over (`drop-target?`) has the dashed outline of the ZapKit variant
   `:drop-target`. `class` is added last."
  [{:keys [collapsed? drop-target? class]}]
  (uu/cm (variants/slot-class (:root styles/kanban-list)
                              {:drop-target (boolean drop-target?)})
         (:section dom-overrides)
         (if collapsed?
           (:section-collapsed dom-overrides)
           (:section-open dom-overrides))
         class))

(defn panel-class
  "Return the classes of the panel of the open column. The panel is hidden while
   the column is collapsed."
  [collapsed?]
  (uu/cm (:panel dom-overrides)
         (if collapsed?
           (:panel-hidden dom-overrides)
           (:panel-visible dom-overrides))))

(defn strip-class
  "Return the classes of the strip of the collapsed column. The strip is hidden
   while the column is open."
  [collapsed?]
  (uu/cm (:strip dom-overrides)
         (if collapsed?
           (:strip-visible dom-overrides)
           (:strip-hidden dom-overrides))))

(defn title-class
  "Return the classes of the title in the header: the ZapKit title that gets an
   ellipsis."
  []
  (variants/slot-class (:title styles/kanban-list) {:collapsed false}))

(defn strip-title-class
  "Return the classes of the title in the strip of a collapsed column: the ZapKit
   title that is written upward."
  []
  (uu/cm (variants/slot-class (:title styles/kanban-list) {:collapsed true})
         (:strip-title dom-overrides)))

(defn body-class
  "Return the classes of the area of the cards."
  []
  (get-in styles/kanban-list [:body :base]))

(defn button-props
  "Return the props of a button that is the trigger of a tooltip. The trigger gives
   props, for example a handler of the click and a class, and the button has props
   of its own. Both click handlers are called, the trigger first. The classes are
   joined. `trigger-props` have the names that React gives (`:onClick`)."
  [{:keys [trigger-props props]}]
  (let [trigger-click (or (:onClick trigger-props)
                          (:on-click trigger-props))
        click (:on-click props)]
    (-> (merge (dissoc trigger-props :onClick :on-click :className :class)
               (dissoc props :on-click :class))
        (assoc :class (uu/cm (:className trigger-props)
                             (:class trigger-props)
                             (:class props))
               :on-click (fn [event]
                           (when (fn? trigger-click)
                             (trigger-click event))
                           (when (fn? click)
                             (click event)))))))

(defn icon-tooltip-button
  "An IconButton with a tooltip. `label` is the text of the tooltip."
  [{:keys [label aria-label icon on-click tab-index expanded?]}]
  [tooltip/as-child
   {:style-variant :tw2
    :side          :top}
   (fn [trigger-props]
     [icon-button/view
      (button-props
       {:trigger-props trigger-props
        :props         (cond-> {:aria-label aria-label
                                :class      "tw2-cursor-pointer"
                                :on-click   on-click
                                :tab-index  tab-index}
                         (some? expanded?)
                         (assoc :aria-expanded expanded?))})
      icon])
   label])

(defn header-view
  "The header of an open column."
  [{:keys [title count collapsed? on-toggle-collapse on-add labels]}]
  [:div
   {:class (uu/cm (get-in styles/kanban-list [:header :base])
                  (:header dom-overrides))}
   [:div
    {:class (:title-group dom-overrides)}
    [:h3
     {:class (title-class)}
     title]
    [badge/view {:variant :secondary}
     count]]
   [:div
    {:class (:actions dom-overrides)}
    [icon-tooltip-button
     {:label      (:collapse labels)
      :aria-label (:collapse-aria labels)
      :expanded?  (not collapsed?)
      :tab-index  (if collapsed? -1 0)
      :on-click   on-toggle-collapse
      :icon       [icons/chevrons-left-icon
                   {:class       (icon-button/icon-class :md)
                    :aria-hidden true}]}]
    (when (fn? on-add)
      [icon-tooltip-button
       {:label      (:add labels)
        :aria-label (:add-aria labels)
        :tab-index  (if collapsed? -1 0)
        :on-click   on-add
        :icon       [icons/plus-icon
                     {:class       (icon-button/icon-class :md)
                      :aria-hidden true}]}])]])

(defn strip-view
  "The strip of a collapsed column. The icon button has the name \"Expand <title> list\".
   The button with the count and the title has no label, so a screen reader reads its
   content and does not read the same name twice."
  [{:keys [title count collapsed? on-toggle-collapse labels]}]
  [:div
   {:class       (strip-class collapsed?)
    :aria-hidden (not collapsed?)
    :inert       (when-not collapsed? "")}
   [icon-tooltip-button
    {:label      (:expand labels)
     :aria-label (:expand-aria labels)
     :expanded?  (when collapsed? false)
     :tab-index  (if collapsed? 0 -1)
     :on-click   on-toggle-collapse
     :icon       [icons/chevrons-right-icon
                  {:class       (icon-button/icon-class :md)
                   :aria-hidden true}]}]
   [:button
    {:type      "button"
     :class     (:strip-button dom-overrides)
     :tab-index (if collapsed? 0 -1)
     :on-click  (fn [event]
                  (when (fn? on-toggle-collapse)
                    (on-toggle-collapse event)))}
    [badge/view {:variant :secondary
                 :class   "tw2-shrink-0 tw2-tabular-nums"}
     count]
    [:span
     {:class (strip-title-class)}
     title]]])

(defn view
  "Render a column of a Kanban board.

   `:title` is the title, and `:count` is the number in the Badge.
   `:collapsed?` makes the strip. The open column shows its children.
   `:on-toggle-collapse` is called when the user collapses or expands the column.
   `:on-add` is called when the user clicks the button that adds an item. A column
   with no `:on-add` has no such button.
   `:labels` has the texts, already translated: `:collapse`, `:expand` and `:add`
   for the tooltips, and `:collapse-aria`, `:expand-aria` and `:add-aria` for the
   screen reader. The texts for the screen reader have the title.
   `:drop-target?` shows that a card is dragged over the column.
   `:class` is added to the column. `:section-props` are other props of the
   element of the column, for example the handlers of the drag and drop.

   The children are the area of the cards. The column does not render them while
   it is collapsed."
  [{:keys [collapsed? drop-target? class section-props] :as props} & children]
  [:section
   (merge section-props
          {:class (section-class {:collapsed?   collapsed?
                                  :drop-target? drop-target?
                                  :class        class})})
   [:div
    {:class       (panel-class collapsed?)
     :aria-hidden (boolean collapsed?)
     :inert       (when collapsed? "")}
    [header-view props]
    (when-not collapsed?
      (into [:div {:class (body-class)}]
            children))]
   [strip-view props]])
